/* eslint-disable react-hooks/exhaustive-deps */
import { CloseOutlined } from '@ant-design/icons'
import { Checkbox, Input, Modal, Select } from 'antd'
import { Rule } from 'antd/es/form'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetTelePhonePrefixes } from '@hooks'

import { StyledForm, StyledRow } from '../../../../../components/StyledComponents'
import useCreateSupplier from '../../../../../hooks/useCreateSupplier'
import useMgmts from '../../../../../hooks/useMgmt'
import { defaultTelephonePreffix } from '../../../../../constants'
import { TelephonePrefix } from '../../../../../types'

const formValidations = {
  businessName: {
    required: 'globals.obligatory_field',
    format: 'supplier_modal.businessName_format_require',
    min: 3,
    max: 20,
  },
  tradeName: {
    required: 'globals.obligatory_field',
    format: 'supplier_modal.tradeName_format_require',
    min: 3,
    max: 20,
  },
  email: { required: 'globals.obligatory_field', format: 'supplier_modal.email_format' },
  cellphone: {
    required: 'globals.obligatory_field',
    format: 'supplier_modal.cellphone_format_require',
    min: 10,
    max: 15,
  },
  sapId: {
    required: 'globals.obligatory_field',
    format: 'supplier_modal.sapId_format_require',
    min: 6,
    max: 6,
  },
  password: {
    required: 'globals.obligatory_field',
    format: 'carrier_modal.password_format_require',
    min: 8,
    regex: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[@$!%*?&._])[A-Za-z0-9@$!%*?&._]{8,}$/,
  },
}

const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const numericRegex = /^[0-9]*$/

type Props = {
  open: boolean
  onClose: () => void
}

const AddSupplierModal: React.FC<Props> = ({ open, onClose }) => {
  const [selectedPreffix, setSelectedPrefix] = useState<number>()
  const { t } = useTranslation()
  const { telephonePrefixes } = useGetTelePhonePrefixes()

  const { mgmts, mgmtsIsLoading } = useMgmts()

  const [form] = StyledForm.useForm()
  const { resetFields } = form

  const { createSupplier } = useCreateSupplier(onClose)

  const validateEmail = (_rule: Rule, value: string) => {
    if (!value) {
      return Promise.reject(t(formValidations.email.required))
    }
    if (!regex.test(value)) {
      return Promise.reject(t(formValidations.email.format))
    }
    return Promise.resolve()
  }
  interface ValidationRule {
    validator: (_rule: Rule, value: string) => Promise<void>
  }

  const validateAlphanumeric: ValidationRule['validator'] = (_rule, value) => {
    if (!value || /^[a-zA-ZáéíóúÁÉÍÓÚñÑ0-9\s]+$/.test(value)) {
      return Promise.resolve()
    }
    return Promise.reject(new Error(t('supplier_modal.format_require')))
  }

  const [isInternalSupplier, setIsInternalSupplier] = useState<boolean>(false)
  const handleIsInternalSupplier = () => {
    setIsInternalSupplier(state => !state)
  }

  const onOk = () => {
    form
      .validateFields()
      .then(() => {
        const formData = form.getFieldsValue()
        const { confirmPassword, ...dataToSend } = formData
        createSupplier(dataToSend)
        // createSupplier({ ...dataToSend, prefixId: selectedPreffix })
        setIsInternalSupplier(false)
      })
      .catch(console.error)
  }

  useEffect(() => {
    if (!open) {
      resetFields()
      setIsInternalSupplier(false)
    }
  }, [open])

  useEffect(() => {
    if (!selectedPreffix) {
      const defaultPreffix = telephonePrefixes.find(
        ({ prefix }) => prefix === defaultTelephonePreffix,
      )
      setSelectedPrefix(defaultPreffix ? defaultPreffix.id : telephonePrefixes[0]?.id)
    }
  }, [telephonePrefixes])

  return (
    <Modal
      open={open}
      keyboard
      maskClosable
      onCancel={() => {
        resetFields()
        setIsInternalSupplier(false)
        onClose()
      }}
      title={t('supplier_modal.title')}
      closeIcon={<CloseOutlined />}
      okText={t('supplier_modal.button_add')}
      cancelText={t('supplier_modal.button_cancel')}
      onOk={onOk}
      destroyOnClose
    >
      <StyledForm form={form} initialValues={{ country: 'AR' }}>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="tradeName"
            label={t('supplier_modal.trade_name')}
            rules={[
              { required: true, message: t('globals.obligatory_field') },
              { validator: validateAlphanumeric },
              {
                min: formValidations.tradeName.min,
                max: formValidations.tradeName.max,
              },
            ]}
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row" style={{ display: 'none' }}>
          <StyledForm.Item
            name="country"
            label={t('supplier_modal.country')}
            rules={[]}
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>

        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="businessName"
            label={t('supplier_modal.business_name')}
            rules={[
              { required: true, message: t('globals.obligatory_field') },
              { validator: validateAlphanumeric },
              {
                min: formValidations.tradeName.min,
                max: formValidations.tradeName.max,
              },
            ]}
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>

        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="email"
            label={t('supplier_modal.email')}
            rules={[{ validator: validateEmail }]}
            required
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>

        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="cellphone"
            label={t('supplier_modal.cellphone')}
            required
            rules={[
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.reject(t(formValidations.cellphone.required))
                  }
                  if (!numericRegex.test(value)) {
                    return Promise.reject(t(formValidations.cellphone.format))
                  }
                  if (
                    value.length < formValidations.cellphone.min ||
                    value.length > formValidations.cellphone.max
                  ) {
                    return Promise.reject(
                      t(formValidations.cellphone.format, {
                        min: formValidations.cellphone.min,
                        max: formValidations.cellphone.max,
                      }),
                    )
                  }
                  return Promise.resolve()
                },
              },
            ]}
            hasFeedback
          >
            <Input
              addonBefore={
                <Select
                  value={selectedPreffix}
                  style={{ width: 100 }}
                  onChange={value => setSelectedPrefix(value)}
                >
                  {telephonePrefixes?.map((item: TelephonePrefix) => (
                    <Select.Option key={item.prefix} value={item.id}>
                      {`+${item.prefix}${item.prefix === '54' ? ' (AR)' : ''}`}
                    </Select.Option>
                  ))}
                </Select>
              }
              onChange={e => {
                const enteredValue = e.target.value
                form.setFieldValue('cellphone', enteredValue)
              }}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row" justify="space-between" align="middle">
          <StyledForm.Item label={t('supplier_modal.internal_supplier')}>
            <Checkbox onChange={handleIsInternalSupplier} />
          </StyledForm.Item>
        </StyledRow>
        {!isInternalSupplier && (
          <StyledRow className="carrier-form-row">
            <StyledForm.Item
              name="sapId"
              label={t('supplier_modal.Sap_code')}
              required
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.reject(t(formValidations.sapId.required))
                    }
                    if (!numericRegex.test(value)) {
                      return Promise.reject(t(formValidations.sapId.format))
                    }
                    if (
                      value.length < formValidations.sapId.min ||
                      value.length > formValidations.sapId.max
                    ) {
                      return Promise.reject(
                        t(formValidations.sapId.format, {
                          min: formValidations.sapId.min,
                          max: formValidations.sapId.max,
                        }),
                      )
                    }
                    return Promise.resolve()
                  },
                },
              ]}
              hasFeedback
            >
              <Input type="number" />
            </StyledForm.Item>
          </StyledRow>
        )}
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="password"
            label={t('carrier_modal.password')}
            rules={[
              { required: true, message: t('globals.obligatory_field') },
              {
                min: formValidations.password.min,
                message: t('carrier_modal.password_min_length_error', {
                  min: formValidations.password.min,
                }),
              },
              {
                pattern: formValidations.password.regex,
                message: t(formValidations.password.format),
              },
            ]}
            hasFeedback
          >
            <Input.Password />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="confirmPassword"
            label={t('carrier_modal.confirm_password')}
            dependencies={['password']}
            rules={[
              { required: true, message: t('globals.obligatory_field') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(t('carrier_modal.password_mismatch_error'))
                },
              }),
            ]}
            hasFeedback
          >
            <Input.Password />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="mgmtId"
            label={t('supplier_modal.zone')}
            rules={[{ required: true, message: t('globals.obligatory_field') }]}
            hasFeedback
          >
            <Select
              showSearch
              style={{ width: '100%' }}
              placeholder={t('supplier_modal.select_zone')}
              options={mgmts?.map(({ description, id }) => ({
                value: id,
                label: description,
              }))}
              loading={mgmtsIsLoading}
              optionFilterProp="label"
            />
          </StyledForm.Item>
        </StyledRow>
      </StyledForm>
    </Modal>
  )
}

export { AddSupplierModal }
